import React from 'react';
import { loadResults, earningsWorkbook, deductionsWorkbook } from '../data/mockData';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const loadedEe = loadResults.filter(r => r.earningsStatus === 'LOADED').length;
const blockedEe = loadResults.filter(r => r.earningsStatus === 'BLOCKED').length;
const totalEe = loadResults.length;

const loadedEarRows = earningsWorkbook.filter(r => r.status !== 'CRITICAL').length;
const blockedEarRows = earningsWorkbook.filter(r => r.status === 'CRITICAL').length;
const loadedDedRows = deductionsWorkbook.filter(r => r.status !== 'CRITICAL').length;

const pieData = [
  { name: 'Loaded', value: loadedEe, color: '#1a6b3c' },
  { name: 'Blocked', value: blockedEe, color: '#b81c1c' },
];

export default function Reconciliation() {
  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h2>Reconciliation Report</h2>
          <p>Post-load comparison · Run EMP-00127-ACME_20260415_143022Z</p>
        </div>
        <span className="badge badge-high" style={{ padding: '6px 14px' }}>⚠ Partial Load</span>
      </div>

      <div className="grid-2 section-gap">
        {/* Pie chart */}
        <div className="card">
          <div className="card-header"><h2>Employee Load Distribution</h2></div>
          <div className="card-body" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={90}
                  dataKey="value" paddingAngle={3} label={({ name, value }) => `${name}: ${value}`}
                  labelLine={false}>
                  {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', gap: 24, marginTop: 8 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 700, color: 'var(--success)' }}>{loadedEe}</div>
                <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>Loaded</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 700, color: 'var(--error)' }}>{blockedEe}</div>
                <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>Blocked</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 700 }}>{totalEe}</div>
                <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>Total</div>
              </div>
            </div>
          </div>
        </div>

        {/* Metrics */}
        <div className="card">
          <div className="card-header"><h2>Reconciliation Metrics</h2></div>
          <div className="card-body">
            {[
              ['Payload Employee Count', totalEe],
              ['Employees Loaded', `${loadedEe} / ${totalEe}`],
              ['Employees Blocked', blockedEe],
              ['Earning Rows Submitted', loadedEarRows],
              ['Earning Rows Blocked', blockedEarRows],
              ['Deduction Rows Submitted', loadedDedRows],
              ['Gross Loaded', '$16,955.75'],
              ['Source Total', '$17,012.00'],
              ['Variance', '−$56.25 ⚠'],
              ['RowStamp Refreshes', '0'],
              ['Duplicate Skips', '0'],
              ['API Errors', '0'],
            ].map(([label, value]) => (
              <div key={label} className="recon-metric">
                <span className="rm-label">{label}</span>
                <span className="rm-value" style={{
                  color: String(value).includes('−') ? 'var(--error)' : String(value).includes('0') && label !== 'Variance' ? 'var(--ink-3)' : undefined
                }}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="card section-gap">
        <div className="card-header"><h2>Load Completion Progress</h2></div>
        <div className="card-body">
          {[
            { label: 'Employee Coverage', num: loadedEe, den: totalEe },
            { label: 'Earnings Rows Loaded', num: loadedEarRows, den: earningsWorkbook.length },
            { label: 'Deductions Rows Loaded', num: loadedDedRows, den: deductionsWorkbook.length },
          ].map(({ label, num, den }) => (
            <div key={label} style={{ marginBottom: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 5 }}>
                <span style={{ fontWeight: 600 }}>{label}</span>
                <span style={{ color: 'var(--ink-3)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                  {num} / {den} ({((num / den) * 100).toFixed(0)}%)
                </span>
              </div>
              <div className="progress-bar-wrap">
                <div className="progress-bar-fill" style={{ width: `${(num / den) * 100}%`, background: num === den ? 'var(--success)' : 'var(--warning)' }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Discrepancy */}
      <div className="card">
        <div className="card-header"><h2>Discrepancy Log</h2></div>
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr><th>Type</th><th>Item</th><th>Expected</th><th>Actual</th><th>Variance</th><th>Action</th></tr>
            </thead>
            <tbody>
              <tr>
                <td><span className="badge badge-critical">CRITICAL</span></td>
                <td>Gross Earnings Total</td>
                <td className="mono">$17,012.00</td>
                <td className="mono">$16,955.75</td>
                <td className="mono" style={{ color: 'var(--error)', fontWeight: 700 }}>−$56.25</td>
                <td style={{ fontSize: 12, color: 'var(--ink-3)' }}>Reparse Maria Santos source EML for missing holiday row</td>
              </tr>
              <tr>
                <td><span className="badge badge-critical">CRITICAL</span></td>
                <td>Carlos Vega — Load Status</td>
                <td className="mono">LOADED</td>
                <td className="mono" style={{ color: 'var(--error)' }}>BLOCKED</td>
                <td className="mono" style={{ color: 'var(--error)', fontWeight: 700 }}>—</td>
                <td style={{ fontSize: 12, color: 'var(--ink-3)' }}>Resolve GUID_REQUIRED before re-run</td>
              </tr>
              <tr>
                <td><span className="badge badge-high">HIGH</span></td>
                <td>Darnell Harris Garnishment D03</td>
                <td className="mono">$524.38</td>
                <td className="mono">$520.31</td>
                <td className="mono" style={{ color: 'var(--high)', fontWeight: 700 }}>−$4.07</td>
                <td style={{ fontSize: 12, color: 'var(--ink-3)' }}>Verify CCPA disposable earnings base</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
